// Engine do Laboratório fee-based. O cálculo é inteiramente aritmético, roda
// 100% client-side e foi portado verbatim do protótipo validado por John —
// NÃO alterar as fórmulas.
//
// As FÓRMULAS continuam puras: recebem `P` (premissas) e devolvem número. O que
// deixou de ser puro é a montagem de `P` — `premissasDoEscritorio()`,
// `freshParams()` e `defaultLabState()` leem a store, porque desde a spec-017 a
// premissa é do ESCRITÓRIO e vem do backend, não uma constante do bundle.

import store from '@state/store'
import { PRESETS, K } from '@modules/fee-fixo/constants/feeLab'
import {
  CANAL_PADRAO,
  canalNormalizado,
  regimeDoCanal,
} from '@modules/fee-fixo/constants/baldes'
import {
  PLATAFORMA_PADRAO,
  plataformaNormalizada,
  taxaDoCenario,
} from '@modules/fee-fixo/constants/plataformas'

// A única classe que o escalar `frac` (pré-spec-021) modulava. O nome existe
// para que o fallback de cenário antigo diga POR QUE só ela: o `DEF` do lab
// tinha uma fração transacional só, e ela era do Equity ativo.
const CLASSE_DO_FRAC_ESCALAR = 'eq'

/**
 * Premissas cuja AUSÊNCIA num cenário salvo é INFORMAÇÃO, não lacuna ([D5.2]).
 *
 * O motor abaixo já trata a ausência delas — sem `custo_cheio` toda classe cai
 * no take rate efetivo, que é a fórmula com que o cenário antigo foi gerado. Mas
 * quem RECONSTRÓI o cenário a partir dos `inputs` salvos precisa preservar essa
 * ausência: completar o `P` congelado com o default de hoje faz o motor calcular
 * certo sobre uma premissa que aquele cenário nunca teve, e a análise entregue
 * ao cliente reabre com outro número.
 *
 * Exportada porque o reconstrutor mora na view (`paramsFromAnaliseInputs`) e a
 * regra é do MOTOR: redigitar a lista lá criaria duas fontes que divergem na
 * primeira premissa nova.
 */
export const PREMISSAS_COM_AUSENCIA_SIGNIFICATIVA = [
  'custo_cheio',
  'frac_classe',
  // As 3 da cadeia do repasse (spec-022). Mesma regra, motivo idêntico: sem
  // elas o `buildAdvisor` reproduz o modelo antigo (knob único), que é a conta
  // com que o cenário foi salvo. Completá-las com o default de hoje faria a
  // receita do assessor de uma análise já apresentada cair ~3× ao reabrir.
  'repasse_plataforma',
  'imposto_repasse',
  'repasse_profissional',
]

/**
 * As 3 premissas da cadeia do repasse, na ORDEM em que o dinheiro anda
 * (spec-022, [US-3]) — espelha `premissas.CAMPOS_DA_CADEIA_DO_REPASSE`.
 */
export const CAMPOS_DA_CADEIA_DO_REPASSE = [
  'repasse_plataforma',
  'imposto_repasse',
  'repasse_profissional',
]

// O valor, se for número utilizável; `null` caso contrário. `NaN`/`Infinity`
// ficam de fora porque eles se propagam calados até a tela ("R$ NaN/ano"), e
// premissa vinda de JSONB salvo nem sempre é número.
const numeroUtilizavel = (valor) => {
  return typeof valor === 'number' && Number.isFinite(valor) ? valor : null
}

/**
 * Se o cenário traz as 3 premissas da cadeia, todas numéricas.
 *
 * Basta uma faltar para a resposta ser `false`: meia cadeia produziria um
 * número plausível calculado com um fator que ninguém escolheu.
 */
export const temCadeiaDoRepasse = (P) => {
  if (!P) return false
  return CAMPOS_DA_CADEIA_DO_REPASSE.every((campo) => {
    return numeroUtilizavel(P[campo]) !== null
  })
}

/**
 * A cadeia do repasse: quanto do custo do cliente chega ao PROFISSIONAL.
 *
 * Porta do `core/receita_profissional.py` — mesmas chaves, mesma ordem de
 * multiplicação (`test_paridade_motor_cadeia_do_repasse.py` é o forçador).
 *
 * - **comissionado**: a plataforma captura o take rate e repassa uma fração ao
 *   escritório; o escritório apura imposto e repassa uma fração do líquido ao
 *   profissional;
 * - **fee (assessoria)**: o cliente paga o fee do profissional, que chega
 *   INTEIRO ao escritório e segue pelos dois mesmos degraus;
 * - **consultoria CVM**: o consultor também apura imposto e divide com quem
 *   atende — `fee_prof × (1 − imposto) × repasse`. O que ele NÃO tem é a
 *   plataforma repassando take rate.
 *
 *   Reversão de 2026-08-10, ancorada no relatório: a p.27 publica "60 bps de
 *   fee próprio, que viram cerca de 48 líquidos na ponta do dono e cerca de 22
 *   na do associado com o mesmo repasse", e a legenda da Tabela 11 — do canal
 *   consultor CVM — declara os dois fatores. Isto restaura o que a [T203] da
 *   spec-022 publicava e reverte a decisão de 2026-08-03. O slider "Repasse" e
 *   o imposto voltam a mover os TRÊS regimes; `repasse_plataforma` continua só
 *   no comissionado, porque na consultoria não há plataforma repassando nada.
 *
 * `fee_plat` NÃO desconta a perna do fee: ele é o que a plataforma cobra do
 * CLIENTE no canal assessoria (guia v3, p.21 — 134 = 119 + 15), não retenção
 * sobre o profissional. O `buildAdvisor` antigo fazia `fee_prof − fee_plat`, e
 * assim os mesmos 15 bps eram contados duas vezes a favor da plataforma.
 *
 * Golden G2 (áudio 2, 2026-07-31): com 72 bps de captura,
 * 72 × (28,5/72) = 28,5 · × 0,801 = 22,8285 · × 0,45 = **10,272825 bps**.
 *
 * @param {Object} P Premissas do cenário.
 * @param {number} capturaInvestimentosBps Take rate de INVESTIMENTOS que a
 *   plataforma captura, sem os não-investimentos (`com.capt`). Repassar sobre a
 *   fatia do gestor daria ao profissional comissão sobre dinheiro que nunca
 *   passou pela plataforma.
 * @returns {Object|null} Os degraus da cadeia em bps, ou `null` quando o
 *   cenário não a modela — nunca zero, que passaria por receita real.
 */
export const receitaDoProfissional = (P, capturaInvestimentosBps) => {
  if (!temCadeiaDoRepasse(P)) return null
  const captura = numeroUtilizavel(capturaInvestimentosBps)
  const feeAoEscritorio = numeroUtilizavel(P.fee_prof)
  if (captura === null || feeAoEscritorio === null) return null

  const liquidoDoImposto = 1 - P.imposto_repasse
  const repasseAoEscritorio = captura * P.repasse_plataforma
  const liquidoDeImposto = repasseAoEscritorio * liquidoDoImposto

  // ★ 2026-08-11, decisão do owner: a taxa da plataforma sai de DENTRO do fee
  // cobrado, e não mais por cima do cliente. Ela deixou de somar no custo do
  // assessorado (`custoFeeDecomposto`) e passou a reduzir o que chega ao
  // profissional: dos 60 bps que o cliente paga, 15 ficam com a plataforma e
  // 45 seguem a cadeia.
  //
  // Só a perna da ASSESSORIA desconta. A consultoria CVM não tem taxa de
  // plataforma, e é por isso que `consultoria_bps` continua correndo sobre o
  // fee cheio — descontar nas duas faria o consultor pagar por um serviço que
  // ele não contrata.
  const taxaDaPlataforma = numeroUtilizavel(P.fee_plat) || 0
  const feeDoAssessor = Math.max(0, feeAoEscritorio - taxaDaPlataforma)
  const feeLiquidoDeImposto = feeDoAssessor * liquidoDoImposto
  const consultoriaLiquidaDeImposto = feeAoEscritorio * liquidoDoImposto

  return {
    captura_investimentos_bps: captura,
    repasse_ao_escritorio_bps: repasseAoEscritorio,
    liquido_de_imposto_bps: liquidoDeImposto,
    comissionado_bps: liquidoDeImposto * P.repasse_profissional,
    // Já SEM a taxa da plataforma — invertido em 11/08/2026. Até então
    // `fee_plat` era acréscimo ao cliente e o escritório recebia os 60 cheios
    // ([US-4] da spec-022); agora o cliente paga 60 e a plataforma retém 15.
    fee_ao_escritorio_bps: feeDoAssessor,
    taxa_da_plataforma_bps: taxaDaPlataforma,
    fee_liquido_de_imposto_bps: feeLiquidoDeImposto,
    fee_bps: feeLiquidoDeImposto * P.repasse_profissional,
    // ★ O degrau intermediário da CONSULTORIA, publicado desde 11/08/2026.
    // Até então ele coincidia com `fee_liquido_de_imposto_bps` e a tela lia
    // aquele; quando a perna do fee passou a descontar a taxa da plataforma, o
    // valor do consultor (48,06) sumiu do payload e a tela passaria a mostrar
    // 36,045 — o número do assessor — na cadeia do consultor, sem erro nenhum.
    consultoria_liquida_de_imposto_bps: consultoriaLiquidaDeImposto,
    // A consultoria segue os MESMOS dois degraus do escritório (2026-08-10,
    // p.27 do relatório): "60 bps de fee próprio, que viram cerca de 48
    // líquidos na ponta do dono e cerca de 22 na do associado com o mesmo
    // repasse" — e a legenda da Tabela 11 declara os dois fatores no canal
    // consultor CVM.
    //
    // ★ Deixou de coincidir com `fee_bps` em 11/08/2026: a perna do fee passou
    // a descontar a taxa da plataforma e esta não. Continua sendo chave
    // própria pela MESMA razão de antes — regimes diferentes —, agora com
    // números diferentes, que é o que torna a separação visível.
    consultoria_bps: consultoriaLiquidaDeImposto * P.repasse_profissional,
  }
}

/**
 * Premissas de um cenário SALVO, sem contrabandear as de hoje.
 *
 * O merge com o default existe para cenário antigo que não gravou um campo
 * qualquer (`cb_dev`, por exemplo) continuar calculável. O que ele não pode
 * fazer é preencher os campos cuja ausência TEM significado.
 *
 * @param {Object} padrao premissas do escritório hoje (`freshParams()`).
 * @param {Object} salvas `inputs.P` congelado na análise.
 * @returns {Object} premissas do cenário, com a ausência preservada.
 */
export const premissasDoCenarioSalvo = (padrao, salvas) => {
  const P = { ...padrao, ...salvas }
  PREMISSAS_COM_AUSENCIA_SIGNIFICATIVA.forEach((campo) => {
    if (Object.prototype.hasOwnProperty.call(salvas || {}, campo)) return
    delete P[campo]
  })
  return P
}

/**
 * Parcela do take rate da classe que depende de transação (guia v3, p.15).
 *
 * Cenário salvo ANTES da spec-021 não tem `frac_classe`, só o escalar `frac` — e
 * ele modulava exclusivamente o Equity ativo. O fallback reproduz isso ao pé da
 * letra (0 nas demais classes) porque a fidelidade ao que o engine gravou é
 * gate: uma análise entregue ao cliente em 2025 tem de continuar produzindo os
 * mesmos números em 2026.
 */
const fracaoTransacional = (P, k) => {
  const porClasse = P.frac_classe
  if (porClasse && porClasse[k] !== null && porClasse[k] !== undefined) {
    return porClasse[k]
  }
  return k === CLASSE_DO_FRAC_ESCALAR ? P.frac : 0
}

// Take rate efetivo da classe depois da contenção do giro. A forma da expressão
// é a do engine original (`trEqEf`), e não a álgebra equivalente
// `tr × (1 − frac × (1 − giro))`: associação diferente muda o último bit, e o
// gate de retrocompatibilidade é igualdade com o que o engine gravou. Com
// `frac = 0` ela devolve `tr` exato, que é o que as classes de estoque precisam.
const trEfClasse = (P, k, giro) => {
  const tr = P.tr[k]
  const frac = fracaoTransacional(P, k)
  return tr * (1 - frac) + tr * frac * giro
}

/**
 * O que sai do BOLSO do cliente na classe, em bps (guia v3, Tabela 1, p.12).
 *
 * Duas naturezas, e é a PRESENÇA de `custo_cheio` que declara qual:
 *
 * - classe de ESTOQUE (fundos, previdência, ETF): o cliente paga a taxa de
 *   administração, que não muda com o giro. O `custo_cheio` é essa taxa — 200 no
 *   fundo ativo, dos quais a plataforma captura 47 e o gestor retém 153;
 * - classe TRANSACIONAL (Equity ativo, renda fixa, float): não há taxa de
 *   administração, o custo É o take rate, e ele encolhe com o giro contido.
 *   `custo_cheio` ausente diz exatamente isso.
 *
 * O fallback é o que mantém cenário salvo antes da spec-021 intacto: sem
 * `custo_cheio` nenhum, TODA classe cai no take rate efetivo — a fórmula que o
 * engine gravou.
 */
/**
 * A classe é de ESTOQUE (cobra taxa de administração) ou TRANSACIONAL (o custo
 * dela É o take rate, que encolhe com o giro)?
 *
 * Exportada porque a TELA precisa da mesma resposta: o tooltip da taxa bruta no
 * card de estrutura da carteira diz o que aquele número é, e ele decidia a
 * natureza comparando a bruta no giro do lado com a bruta no giro integral — um
 * proxy que vale para TODA classe no lado comissionado, onde o giro já é
 * integral. O Equity ativo, que não tem taxa de administração nenhuma, passava
 * a se anunciar como se tivesse.
 *
 * É a condição, não a fórmula: quem quiser o VALOR continua chamando o motor.
 * Duas cópias desta condição concordariam hoje e divergiriam no dia em que só
 * uma fosse corrigida — o modo de falha que a T208 pagou para aprender.
 */
export const classeDeEstoque = (P, k) => {
  const cheio = P && P.custo_cheio
  return Boolean(cheio && cheio[k] !== null && cheio[k] !== undefined)
}

const custoBolsoClasse = (P, k, giro) => {
  if (classeDeEstoque(P, k)) return P.custo_cheio[k]
  return trEfClasse(P, k, giro)
}

// Take rate ponderado de investimentos: o que a PLATAFORMA captura — não o que o
// cliente paga. A distinção nasceu na spec-021; até ela os dois eram o mesmo
// número, e o custo comissionado saía 117 em vez de 164.
const trInv = (P, alc, giro) => {
  return K.reduce((s, k) => s + alc[k] * trEfClasse(P, k, giro), 0)
}

// Custo dos produtos no BOLSO do cliente, ponderado pela alocação.
const custoBolsoBps = (P, alc, giro) => {
  return K.reduce((s, k) => s + alc[k] * custoBolsoClasse(P, k, giro), 0)
}

// Cashback ponderado (bps) para uma alocação.
const cashbackBps = (P, alc) => {
  return K.reduce((s, k) => s + alc[k] * P.cb[k], 0)
}

// Interpola linearmente duas alocações (t entre 0 e 1).
const interp = (a, b, t) => {
  const o = {}
  K.forEach((k) => {
    o[k] = a[k] + (b[k] - a[k]) * t
  })
  return o
}

// Custo líquido (bolso − cashback) de uma classe já no alvo fee — a coluna
// "líquido" da Tabela 1 do guia. É o que o painel de alocação rotula como
// "TR líq"; ele e a coluna do relatório precisam dizer o mesmo número.
export const trLiqClasse = (P, k) => {
  return custoBolsoClasse(P, k, P.giro_fim) - P.cb[k]
}

/**
 * Custo comissionado no BOLSO do cliente, e para onde vai cada parcela.
 *
 * O guia v3 publica 164 bps: 117 capturados pela plataforma e 47 retidos pelos
 * gestores de fundos. A decomposição é ADITIVA por construção — a fatia do
 * gestor é a DIFERENÇA entre o que sai do bolso e o que a plataforma captura,
 * nunca um terceiro número calculado à parte. Duas parcelas por caminhos
 * independentes concordariam com o guia cada uma de um jeito e não fechariam
 * entre si.
 *
 * O giro do comissionado é 1 — integral, o retrato de hoje: nada foi contido
 * ainda, e é contra ele que o `giro` das demais contas é razão.
 *
 * @param {Object} P Premissas do cenário.
 * @returns {Object} Total no bolso, captura da plataforma, fatia dos gestores e
 *   não-investimentos.
 */
/**
 * O rebate de distribuição, limitado ao que a fatia fora da plataforma comporta.
 *
 * **Sem `custo_cheio`, não há de onde tirá-lo.** No cenário anterior à spec-021
 * o custo do bolso É a captura, a diferença sai zero e não existe camada de
 * gestor para separar em duas. Subtrair o rebate ali produziria uma fatia de
 * gestor NEGATIVA — número que o motor não apurou e que a tela desenharia
 * abaixo do eixo. O piso em zero preserva o comportamento daquela época.
 *
 * Espelha `core/relatorio_projecao.py::_rebate_que_cabe_na_fatia`.
 */
const rebateQueCabeNaFatia = (rebateBps, fatiaForaDaPlataformaBps) =>
  Math.max(0, Math.min(rebateBps, fatiaForaDaPlataformaBps))

export const custoComissionadoDecomposto = (P) => {
  const alc = P.alc_c
  const ni = P.ni_comis
  const captura = trInv(P, alc, 1) + ni
  const bolso = custoBolsoBps(P, alc, 1) + ni
  // Bugfix 2026-08-07: dentro de `bolso − captura` há DUAS coisas com donos
  // diferentes. O guia v3 p.21 abre os 200 bps do fundo ativo em "uma fica com
  // o gestor, na casa de 110 bps. Outra fica com a plataforma, cerca de 47 bps.
  // E a terceira iria à assessoria como remuneração de distribuição, cerca de
  // 45 bps" — e no comissionado essa terceira não volta ao cliente (p.11), fica
  // retida. Publicá-la dentro de `fatia_gestores_bps` fazia a tela dizer ao
  // cliente que ela é do gestor do fundo.
  const rebate = rebateQueCabeNaFatia(cashbackBps(P, alc), bolso - captura)
  return {
    custo_comissionado_bps: bolso,
    captura_plataforma_bps: captura,
    fatia_gestores_bps: bolso - captura - rebate,
    rebate_distribuicao_bps: rebate,
    nao_investimentos_bps: ni,
  }
}

// Custo total do modelo comissionado. `total` é o BOLSO (o que o cliente paga);
// `capt` continua sendo a captura de INVESTIMENTOS da plataforma, que é a base
// do repasse ao assessor — repassar sobre a fatia do gestor lhe daria comissão
// sobre dinheiro que nunca passou pela plataforma. `prod` é o custo dos produtos
// no bolso, que é o que fecha a decomposição do cliente.
//
// [T110, review MINOR-4] Renomeado de `ti` para `capt` — `ti` aqui e `ti` de
// `costBreak.comis` (decompRegime, abaixo) significavam coisas DIFERENTES (a
// captura da plataforma vs. o custo dos produtos no bolso) e os dois objetos
// circulavam juntos no mesmo `resultado`. `capt` é o nome que `costBreak` já
// usa com este exato sentido. Sem leitor no backend (`resultados['com']` não é
// lido em `core/*.py`); os únicos leitores eram `buildAdvisor` (abaixo) e
// `fee-lab-regime-cards.vue` — os dois atualizados no mesmo commit.
const comisTotal = (P) => {
  const d = custoComissionadoDecomposto(P)
  const ni = d.nao_investimentos_bps
  return {
    capt: d.captura_plataforma_bps - ni,
    prod: d.custo_comissionado_bps - ni,
    gest: d.fatia_gestores_bps,
    reb: d.rebate_distribuicao_bps,
    ni,
    total: d.custo_comissionado_bps,
  }
}

/**
 * Custo fee-based num ano da migração, e para onde vai cada parcela.
 *
 * O total é o MESMO nos dois canais desde 11/08/2026: produtos + fee do
 * profissional − cashback. A taxa da plataforma sai de DENTRO do fee no canal
 * assessoria, em vez de ser acrescentada ao cliente — quem absorve os 15 bps é
 * a receita do profissional (`receitaDoProfissional`), não o bolso de quem
 * paga. `custo_fee_assessor_bps` continua publicada porque o payload e o PDF a
 * leem, e hoje vale exatamente `custo_fee_bps`.
 *
 * @param {Object} P Premissas do cenário.
 * @param {Object} alcTarget Alocação-alvo do fee.
 * @param {number} ano Ano da projeção, a partir de 1.
 * @param {number} anosMig Prazo de migração em anos.
 * @param {number} cbFactor Parcela do cashback devolvida ao cliente (0..1).
 * @returns {Object} Total, parcelas e o custo por classe (cheio e líquido).
 */
export const custoFeeDecomposto = (P, alcTarget, ano, anosMig, cbFactor) => {
  const t = Math.min(ano / anosMig, 1)
  const alc = interp(P.alc_c, alcTarget, t)
  const giro = P.giro_ini - (P.giro_ini - P.giro_fim) * t
  const produtos = custoBolsoBps(P, alc, giro)
  const ni = P.ni_comis - (P.ni_comis - P.ni_fb) * t
  // O rebate BRUTO é o que a plataforma recebe da gestora; `cb` é a parte dele
  // que chega de volta ao cliente. Com devolução integral os dois coincidem;
  // com `cb_dev < 1` a diferença fica com quem distribuiu. O balde precisa do
  // bruto para saber o que ENTROU na plataforma, não só o que ela devolveu.
  const rebateBruto = cashbackBps(P, alc)
  const rebate = rebateQueCabeNaFatia(rebateBruto, produtos - trInv(P, alc, giro))
  // O cashback sai do rebate BRUTO, não do limitado: o que o cliente recebe de
  // volta é o que a premissa `cb` promete, independente de o cenário modelar ou
  // não a camada do gestor. Limitar aqui mudaria o custo do cliente — e o
  // bugfix do rebate reparte, não recalcula.
  const cb = rebateBruto * cbFactor
  // `fee_plat` não é obrigatório no cenário salvo: sem ele, é canal consultor.
  const feePlat = P.fee_plat || 0
  const total = produtos + ni + P.fee_prof - cb

  const cheioPorClasse = {}
  const liquidoPorClasse = {}
  K.forEach((k) => {
    cheioPorClasse[k] = custoBolsoClasse(P, k, giro)
    liquidoPorClasse[k] = cheioPorClasse[k] - P.cb[k] * cbFactor
  })

  return {
    custo_fee_bps: total,
    // ★ 2026-08-11, decisão do owner: existe UM fee — o do profissional — e a
    // taxa da plataforma sai de DENTRO dele, em vez de ser acrescentada ao
    // cliente. O assessorado passou a pagar o mesmo que o consultor CVM
    // (119,5208 bps nas premissas padrão, contra 134,5208 antes) e quem
    // absorve os 15 é a receita do profissional (`receitaDoProfissional`).
    // A chave continua existindo porque o payload e o PDF a leem — mudou o
    // valor, não o contrato.
    custo_fee_assessor_bps: total,
    produtos_bps: produtos,
    captura_plataforma_bps: trInv(P, alc, giro),
    fee_profissional_bps: P.fee_prof,
    fee_plataforma_bps: feePlat,
    nao_investimentos_bps: ni,
    cashback_bps: cb,
    rebate_distribuicao_bps: rebate,
    custo_cheio_por_classe_bps: cheioPorClasse,
    custo_liquido_por_classe_bps: liquidoPorClasse,
  }
}

// Custo total do modelo fee-based num ano da migração.
// cbFactor: fração do cashback devolvido (1 = 100% para consultoria, P.cb_dev para assessoria).
//
// ★ 11/08/2026 — o parâmetro `comTaxaDePlataforma` foi REMOVIDO. Ele escolhia
// entre `custo_fee_assessor_bps` e `custo_fee_bps`, e as duas chaves passaram a
// valer o mesmo quando a taxa da plataforma virou retenção por dentro do fee.
// Os dois ramos do ternário retornavam o mesmo número: um parâmetro que não
// decide nada é pior que nenhum, porque sugere um comportamento que não existe.
// O que ainda separa os canais é a RECEITA do profissional, não o custo.
export const fbTotal = (P, alcTarget, ano, anosMig, cbFactor) =>
  custoFeeDecomposto(P, alcTarget, ano, anosMig, cbFactor).custo_fee_bps

// Série anual de custo dos 3 regimes (comissionado / fee-based / consultor).
// cbDev: fração do cashback devolvido pela assessoria (P.cb_dev); consultor devolve 100%.
const buildSeries = (P, com, years, mig, alvo, cbDev) => {
  const series = { comis: [], fee: [], cons: [] }
  years.forEach((y) => {
    if (y === 0) {
      series.comis.push(com.total)
      series.fee.push(com.total)
      series.cons.push(com.total)
      return
    }
    series.comis.push(com.total)
    // As duas séries só divergem pelo CASHBACK: a assessoria devolve `cbDev`
    // (editável por escritório) e a consultoria devolve 100% por dever
    // fiduciário. Com `cb_dev = 1` elas coincidem — e é isso que o teste
    // `series.fee[1] ≈ series.cons[1]` trava. A taxa da plataforma NÃO entra
    // aqui desde 11/08/2026: ela é retida de dentro do fee, e somá-la à série
    // devolveria ao cliente um custo que ele não paga.
    series.fee.push(fbTotal(P, alvo, y, mig, cbDev))
    series.cons.push(fbTotal(P, alvo, y, mig, 1))
  })
  return series
}

/**
 * Decomposição do custo de um regime no último ano (produtos + NI + fee − cb).
 *
 * `ti` é o custo dos PRODUTOS no bolso do cliente — é ele que fecha a soma com
 * `ni`, `fee` e `cb`, e é ele que o relatório em PDF lê como `produtos_bps`
 * (`relatorio_payload._montar_composicao`). `capt` e `gest` abrem esse mesmo
 * número em quanto a plataforma captura e quanto o gestor retém: são uma LEITURA
 * de `ti`, não parcelas adicionais, e somar as três duplicaria o custo.
 *
 * **`costBreak.comis.ti` mudou de GRANDEZA na spec-021, preservando o nome**
 * (corte em 2026-08-02, review [T110]). Antes da recalibração, `ti` era a
 * captura de investimentos da plataforma (75,13 bps, com o total em 117,13);
 * depois, é o custo dos produtos no bolso do cliente (121,95 bps, com o total
 * em 163,95). A escolha foi deliberada — o invariante que o PDF depende
 * (`ti + ni + fee − cb == total`) continua fechando nos dois regimes e nas
 * duas eras —, mas quem comparar `costBreak.comis.ti` entre uma análise salva
 * ANTES do corte e uma DEPOIS está comparando duas grandezas diferentes sob a
 * mesma chave. NÃO confundir com `comisTotal().capt` (renomeado de `ti` para
 * `capt` no MESMO review — MINOR-4): esse é sempre a captura da plataforma, em
 * qualquer era, e nunca foi lido pelo backend.
 *
 * @param {number} cbFactor Fração do cashback devolvido (P.cb_dev na assessoria,
 *   1 no consultor).
 */
const decompRegime = (
  P,
  com,
  alcTarget,
  cbFactor,
  isComis,
  last,
  mig,
  publicarCanalAssessor = false
) => {
  if (isComis) {
    return {
      ti: com.prod,
      capt: com.capt,
      gest: com.gest,
      // `cb` segue 0 aqui — no comissionado nada é DEVOLVIDO ao cliente. `reb`
      // é o mesmo dinheiro visto do outro lado: quanto fica retido com quem
      // distribuiu. As duas chaves não são redundantes, são complementares, e é
      // por confundi-las que a fatia acabava publicada como do gestor.
      reb: com.reb,
      ni: com.ni,
      fee: 0,
      cb: 0,
      total: com.total,
    }
  }
  const d = custoFeeDecomposto(P, alcTarget, last, mig, cbFactor)
  const bloco = {
    ti: d.produtos_bps,
    capt: d.captura_plataforma_bps,
    gest: d.produtos_bps - d.captura_plataforma_bps - d.rebate_distribuicao_bps,
    reb: d.rebate_distribuicao_bps,
    ni: d.nao_investimentos_bps,
    fee: d.fee_profissional_bps,
    cb: d.cashback_bps,
    total: d.custo_fee_bps,
  }
  // [D7-FINAL] O custo do canal ASSESSORIA, publicado ADITIVAMENTE e só na
  // perna do fee. `total` continua sendo o do consultor — que é o manchete da
  // simulação, do relatório e das séries, e o owner confirmou que é assim que
  // deve ser. O que faltava era ter o número do assessorado disponível para a
  // superfície que se chama "Fee fixo · assessoria": o motor já o calculava em
  // `custo_fee_assessor_bps` e ninguém o publicava (guia p.21: 134 = 119 + 15).
  //
  // Chave nova, nenhuma existente muda de nome ou de grandeza — o `costBreak`
  // do cenário salvo é contrato do PDF (`relatorio_payload._montar_composicao`),
  // e o invariante `ti + ni + fee − cb == total` que ele lê continua valendo.
  // O regime ASSESSORIA publica a taxa da plataforma em chave própria
  // (`fee_plat`). Contrato acordado com o par do backend: os 15 NÃO entram em
  // `fee` — `fee` alimenta o balde ASSESSORIA no payload do PDF, e embutir a
  // taxa ali poria dinheiro da plataforma no bolso do escritório na folha
  // entregue ao cliente.
  //
  // ★ 11/08/2026 — o `bloco.total` NÃO é mais sobrescrito aqui. Ele era
  // `d.custo_fee_assessor_bps`, que desde então vale o mesmo que
  // `d.custo_fee_bps`: a atribuição virou no-op e escondia que o custo do
  // cliente deixou de depender do canal. `fee_plat` continua sendo publicada
  // porque é por ela que o backend sabe que 15 dos 60 são da plataforma.
  //
  // Isto NÃO depende de `S.canal`: `costBreak` carrega os três REGIMES
  // comparados, e um regime não muda de significado conforme quem é o cliente.
  // O card "Fee fixo · assessoria" mostra o que um assessorado pagaria mesmo
  // quando o cenário é de consultoria — a comparação é o produto ([D7-FINAL]).
  // O que o canal decide é qual dos três é o CASO do cliente (ver `simulate`).
  if (publicarCanalAssessor) {
    bloco.fee_plat = d.fee_plataforma_bps
  }
  return bloco
}

// Patrimônio final de um regime (comis / fee / cons). Soma o aporte anual
// (mesmo valor pros 3 regimes — dinheiro novo do cliente, não depende de
// qual assessor administra) a cada ano, igual à engine V0 do CTO (projetar()).
const patrOf = (S, com, series, kind) => {
  const aporte = Number(S.aporteAnual) || 0
  let p = S.auc
  for (let y = 1; y <= S.hor; y++) {
    if (kind === 'comis') p = p * (1 + S.rent - com.total / 10000) + aporte
    else if (kind === 'fee')
      p = p * (1 + S.rent + S.alfa - series.fee[y] / 10000) + aporte
    else p = p * (1 + S.rent + S.alfa - series.cons[y] / 10000) + aporte
  }
  return p
}

/**
 * Custo de uma série de bps sobre o AuC: o ACUMULADO do horizonte e o fluxo do
 * ÚLTIMO ano.
 *
 * Os dois saem do MESMO laço porque são a mesma trajetória — o acumulado é a
 * soma dos fluxos anuais e `ultimoAno` é a última parcela dessa soma. Calculá-
 * los em laços separados seria a receita conhecida para o total e a parcela
 * divergirem; com horizonte de 1 ano eles têm de ser idênticos, e há teste
 * fixando isso.
 *
 * @returns {{acc: number, ultimoAno: number}} Ambos em reais.
 */
const custosDaSerie = (S, arr) => {
  let p = S.auc
  let acc = 0
  let ultimoAno = 0
  for (let y = 1; y <= S.hor; y++) {
    ultimoAno = (p * arr[y]) / 10000
    acc += ultimoAno
    p = p * (1 + S.rent - arr[y] / 10000)
  }
  return { acc, ultimoAno }
}

// Primeiro ano em que a série fica <= o custo comissionado (cruzamento).
const findCross = (arr, comTotal, H) => {
  for (let y = 1; y <= H; y++) {
    if (arr[y] <= comTotal + 0.01) return y
  }
  return null
}

// Custos, receita do assessor e agregados — montados a partir da série.
const buildCosts = (P, com, series, last, mig, alvo) => {
  return {
    cost: { comis: com.total, fee: series.fee[last], cons: series.cons[last] },
    costBreak: {
      comis: decompRegime(P, com, null, 0, true, last, mig),
      // Só a perna do fee publica o custo do canal assessoria ([D7-FINAL]).
      fee: decompRegime(P, com, alvo, P.cb_dev, false, last, mig, true),
      cons: decompRegime(P, com, alvo, 1, false, last, mig),
    },
    feeTotal: { comis: null, fee: P.fee_prof, cons: P.fee_prof },
  }
}

/**
 * Receita do profissional por regime, pelo modelo ANTERIOR à spec-022.
 *
 * Preservado verbatim porque é a conta com que os cenários salvos até
 * 2026-08-03 foram apresentados ao cliente ([D5.2]): reabrir um deles tem de
 * reproduzir o número que estava na tela naquele dia, e não o da cadeia. Não é
 * fallback de conveniência — é reprodutibilidade, e `P.repasse` (o knob do lab,
 * que `freshParams` gravava em todo cenário) é o que o identifica.
 */
const advisorDoCenarioSemCadeia = (P, com) => {
  const grossComis = (numeroUtilizavel(P.repasse) || 0) * com.capt
  const grossFee = Math.max(0, P.fee_prof - P.fee_plat)
  const adv = { comis: grossComis, fee: grossFee, cons: P.fee_prof }
  return { adv, advGross: { ...adv }, cadeia: null }
}

/**
 * Receita do profissional nos 3 regimes, pela cadeia do repasse (spec-022).
 *
 * **O que mudou, e por quê.** O modelo anterior aplicava um knob único
 * (`P.repasse`, 0,45) direto sobre a captura INTEIRA — como se tudo o que a
 * plataforma captura chegasse ao escritório e nada fosse tributado. Com as
 * premissas default isso dava 33,8 bps no comissionado contra os 10,7 da
 * cadeia: superestimava a receita do assessor em ~3,15×, que é exatamente
 * 1 / (repasse_plataforma × (1 − imposto)).
 *
 * **`fee_plat` não entra em nenhuma das pernas** ([US-4]). Ele é o que a
 * plataforma cobra do CLIENTE assessorado por cima (134 = 119 + 15, guia p.21),
 * não uma retenção sobre quem atende. Descontá-lo da perna do fee, como se
 * fazia, cobrava os mesmos 15 bps duas vezes a favor da plataforma: uma no
 * bolso do cliente e outra no bolso do profissional.
 *
 * **`cons` sai da perna própria, não da do fee** (owner, 2026-08-03). A
 * spec-022 tinha feito `cons = fee_bps` por ler os dois canais como o mesmo
 * arranjo societário; o owner corrigiu o modelo olhando a tela — na consultoria
 * CVM quem cobra é o profissional, então ele fica com o fee cheio.
 */
const buildAdvisor = (P, com) => {
  const cadeia = receitaDoProfissional(P, com.capt)
  if (!cadeia) return advisorDoCenarioSemCadeia(P, com)
  const adv = {
    comis: cadeia.comissionado_bps,
    fee: cadeia.fee_bps,
    cons: cadeia.consultoria_bps,
  }
  return { adv, advGross: { ...adv }, cadeia }
}

const buildAggregates = (S, com, series, H, regimeDepois) => {
  const patrimonio = {
    comis: patrOf(S, com, series, 'comis'),
    fee: patrOf(S, com, series, 'fee'),
    cons: patrOf(S, com, series, 'cons'),
  }
  // Indexado pela MESMA chave de regime que `series`, `cost` e `adv` usam —
  // é o que permite a quem tem o regime do canal na mão pegar o custo daquele
  // regime sem repetir o `if` do canal.
  const custos = {
    comis: custosDaSerie(S, series.comis),
    fee: custosDaSerie(S, series.fee),
    cons: custosDaSerie(S, series.cons),
  }
  return {
    patrimonio,
    custos,
    accComis: custos.comis.acc,
    accFee: custos.fee.acc,
    accCons: custos.cons.acc,
    // [D8] O cruzamento do CASO do cliente segue o canal; `crossC` continua
    // sendo o do consultor para quem quiser os dois lado a lado. Os KPIs leem
    // `cross`, então trocar o conteúdo aqui é o que faz o painel inteiro
    // acompanhar a escolha sem que cada consumidor repita a decisão — e
    // repetir a decisão em N lugares é como os dois lados divergem.
    cross: findCross(series[regimeDepois], com.total, H),
    crossC: findCross(series.cons, com.total, H),
  }
}

/**
 * Métricas derivadas (economia, ganho, percentuais) a partir dos agregados.
 *
 * [D8] O regime que o canal escolheu é o lado "depois" de toda comparação: é
 * contra ele que a economia do cliente é medida. Recebe o REGIME, e não o
 * acumulado já escolhido, porque a mesma decisão vale para o acumulado e para
 * o fluxo do ano final — resolver o canal duas vezes é como os dois passariam
 * a falar de regimes diferentes.
 *
 * `clienteAnoFinal` (owner, 2026-08-03) é a economia do ÚLTIMO ano do
 * horizonte, o fluxo que os KPIs passaram a exibir. O acumulado continua
 * publicado em `cliente`: quem descreve os 5 anos é o resumo que vai para a
 * oportunidade do CRM, e ele não mudou de período.
 */
const buildDerived = (adv, agg, regimeDepois) => {
  const p = agg.patrimonio
  const accDepois = agg.custos[regimeDepois].acc
  const anoComis = agg.custos.comis.ultimoAno
  const anoDepois = agg.custos[regimeDepois].ultimoAno
  return {
    save: {
      cliente: agg.accComis - accDepois,
      clienteCons: agg.accComis - agg.accCons,
      clienteAnoFinal: anoComis - anoDepois,
    },
    advGain: { fee: adv.fee - adv.comis, cons: adv.cons - adv.comis },
    patrGain: { fee: p.fee - p.comis, cons: p.cons - p.comis },
    pctSave: {
      cliente: agg.accComis ? (agg.accComis - accDepois) / agg.accComis : 0,
      // Contra o custo comissionado DAQUELE ano — comparar o fluxo do ano
      // final com o acumulado de 5 daria um percentual sem significado.
      clienteAnoFinal: anoComis ? (anoComis - anoDepois) / anoComis : 0,
    },
    pctAdv: {
      fee: adv.comis ? (adv.fee - adv.comis) / adv.comis : 0,
      cons: adv.comis ? (adv.cons - adv.comis) / adv.comis : 0,
    },
    pctPatr: { fee: p.comis ? p.fee / p.comis - 1 : 0 },
  }
}

/**
 * As premissas do cenário com `fee_plat` já resolvido pela PLATAFORMA.
 *
 * ★ 12/08/2026 — a taxa da plataforma deixou de ser um número digitado e passou
 * a ser TABELADA (`constants/plataformas.js`): BTG e Safra cobram 30 bps; a XP
 * escalona por patrimônio (20 / 15 / 10). Deixar o consultor digitar 15 quando
 * a plataforma dele cobra 30 produzia um documento com o custo errado sem nada
 * ficar vermelho.
 *
 * **Resolvido UMA vez, aqui — pelo mesmo motivo que o canal.** O motor abaixo
 * inteiro (`custoFeeDecomposto`, `decompRegime`, `buildAdvisor`) continua lendo
 * `P.fee_plat` verbatim: nenhuma das cinco leituras precisa saber que existe
 * uma tabela. Espalhar `taxaDoCenario(...)` por elas criaria cinco lugares onde
 * a tela e o documento podem divergir — que é o defeito que a [D8] pagou para
 * corrigir no canal.
 *
 * Cenário salvo SEM `plataforma` (tudo o que existe hoje) mantém o `fee_plat`
 * gravado, intacto: aqueles documentos foram entregues com aquele número.
 *
 * @param {Object} S Estado do laboratório.
 * @returns {Object} `S.P`, com `fee_plat` da tabela quando há plataforma.
 */
export const premissasComATaxaDaPlataforma = (S) => {
  const P = (S || {}).P || {}
  const taxa = taxaDoCenario(S, P, (S || {}).auc)
  // `null` é AUSÊNCIA (plataforma desconhecida, ou XP sem patrimônio) e não
  // zero: sobrescrever o `fee_plat` do cenário com zero publicaria um custo
  // que ninguém apurou. Sem taxa resolvível, vale o que o cenário trouxe.
  if (taxa === null || taxa === P.fee_plat) return P
  return { ...P, fee_plat: taxa }
}

// Simulação completa: séries anuais + decomposições + agregados + cruzamentos.
export const simulate = (S) => {
  const P = premissasComATaxaDaPlataforma(S)
  const H = S.hor
  const com = comisTotal(P)
  // [D8] O canal do cenário resolvido UMA vez, aqui. Cada consumidor que
  // repetisse `regimeDoCanal(...)` seria mais um lugar onde tela e documento
  // podem divergir — que é o defeito que a spec-022 passou a W3 corrigindo.
  const canal = canalNormalizado(S.canal)
  const regimeDepois = regimeDoCanal(canal)
  const years = []
  for (let y = 0; y <= H; y++) years.push(y)
  const series = buildSeries(P, com, years, S.mig, S.alloc, P.cb_dev)
  const costs = buildCosts(P, com, series, H, S.mig, S.alloc)
  const advisor = buildAdvisor(P, com)
  const agg = buildAggregates(S, com, series, H, regimeDepois)
  const derived = buildDerived(advisor.adv, agg, regimeDepois)
  return {
    years,
    series,
    com,
    H,
    cost: costs.cost,
    costBreak: costs.costBreak,
    feeTotal: costs.feeTotal,
    // [D8] O canal do cenário e o que ele resolve. `canal` viaja para o save
    // (`inputs.canal`) porque o backend LÊ alguns números do cenário e CALCULA
    // outros: sem o marcador, um mesmo PDF sai com capa de uma família e
    // projeção de outra (15 bps de descasamento, medido pelo par do backend).
    canal,
    regimeDepois,
    custoDepois: costs.cost[regimeDepois],
    adv: advisor.adv,
    advGross: advisor.advGross,
    // Os degraus da cadeia (spec-022), `null` em cenário salvo antes dela. É o
    // par em JS do bloco `receita_profissional` do payload — quem quiser MOSTRAR
    // a cadeia lê daqui em vez de refazer a conta na tela.
    advCadeia: advisor.cadeia,
    patrimonio: agg.patrimonio,
    accComis: agg.accComis,
    accFee: agg.accFee,
    accCons: agg.accCons,
    cross: agg.cross,
    crossC: agg.crossC,
    ...derived,
  }
}

export const MSG_PREMISSAS_INDISPONIVEIS =
  'As premissas do escritório não foram carregadas, então a simulação não pode ' +
  'ser calculada. Recarregue a página; se persistir, contate o suporte.'

/**
 * Premissa que não chegou do backend (ADR-017-A).
 *
 * Classe própria para que o consumidor distinga "faltou premissa" de qualquer
 * outra falha e mostre a mensagem certa — um `Error` genérico obrigaria a
 * comparar texto.
 */
export class PremissasIndisponiveisError extends Error {
  constructor(message) {
    super(message || MSG_PREMISSAS_INDISPONIVEIS)
    this.name = 'PremissasIndisponiveisError'
  }
}

/**
 * Premissas do escritório, vindas da store (spec-017 — fonte única).
 *
 * Quem carrega é o shell do módulo (`views/nav-bar/fee-fixo-nav-bar.vue`), uma
 * vez por entrada no módulo; as 4 telas consomem daqui.
 *
 * LANÇA quando não há premissa carregada, de propósito. O `DEF` de `feeLab.js`
 * está a um import de distância e seria o fallback óbvio — e errado: um
 * escritório com take rate próprio veria o diagnóstico calculado com o número
 * da AAWZ, plausível, indistinguível do certo na tela e entregue ao cliente
 * final num PDF. Erro visível é melhor que número errado calado.
 *
 * @returns {Object} Cópia das premissas no vocabulário curto do engine.
 * @throws {PremissasIndisponiveisError} Quando a store não tem premissa.
 */
export const premissasDoEscritorio = () => {
  if (!store.getters['feeFixo/premissasCarregadas']) {
    throw new PremissasIndisponiveisError()
  }
  // Cópia: o consumidor edita `P` à vontade (é o que o Fee Lab faz a cada
  // slider) sem mutar o objeto vivo da store.
  return structuredClone(store.getters['feeFixo/premissas'])
}

// Premissas do escritório + o parâmetro que é do laboratório, não do
// escritório: `cb_dev` é hipótese da simulação (quanto a assessoria devolve do
// rebate), varia por cenário e por isso fica fora das premissas persistidas.
//
// `repasse` saiu daqui na spec-022: ele era o knob solto do lab (0,45 aplicado
// sobre a captura inteira) e virou `repasse_profissional`, premissa versionada
// do ESCRITÓRIO — mesma grandeza, mesmo valor, agora customizável e resolvida
// pelo backend como as demais. O slider da tela continua o mesmo, só apontando
// para o campo novo.
export const freshParams = () => {
  const p = premissasDoEscritorio()
  p.cb_dev = 1
  return p
}

// Estado inicial do laboratório.
export const defaultLabState = () => {
  return {
    P: freshParams(),
    // [D8] O modelo de atendimento do cenário. Default é assessor fee-based,
    // por decisão do owner (2026-08-03) — e é também o canal de custo MAIOR
    // para o cliente, que é o lado seguro de errar numa conversa comercial.
    canal: CANAL_PADRAO,
    // ★ 12/08/2026 — a plataforma do cenário. Ela é que TABELA a taxa
    // (`constants/plataformas.js`); `P.fee_plat` deixou de ser digitado.
    //
    // O default é XP porque, no AuC default de R$ 1 mi, a tabela dela devolve
    // exatamente os 15 bps que o `DEF.fee_plat` já trazia: quem abrir a tela
    // amanhã vê o mesmo número de hoje, e a mudança não reprecifica ninguém
    // por acidente. Trocar de plataforma passa a ser um ato explícito.
    plataforma: PLATAFORMA_PADRAO,
    auc: 1000000,
    hor: 5,
    mig: 5,
    alloc: { ...PRESETS.fb },
    allocKey: 'fb',
    rent: 0.1,
    alfa: 0.01,
    aporteAnual: 0,
    idadeAtual: 34,
    idadeAposentadoria: 65,
    longevidade: 90,
    retiradaAnual: 120000,
  }
}

// Resolve as 3 idades da curva (atual/aposentadoria/longevidade) já
// saneadas (inteiras, cada uma >= a anterior) e a lista de idades da curva.
const resolveIdadesCicloVida = (S) => {
  const idadeAtual = Math.round(Number(S.idadeAtual)) || 0
  const idadeAposentadoria = Math.max(
    idadeAtual,
    Math.round(Number(S.idadeAposentadoria)) || idadeAtual
  )
  const longevidade = Math.max(
    idadeAposentadoria,
    Math.round(Number(S.longevidade)) || idadeAposentadoria
  )
  const ages = []
  for (let a = idadeAtual; a <= longevidade; a++) ages.push(a)
  return { ages, idadeAposentadoria }
}

// Projeta uma série de patrimônio ano a ano (juros compostos + aporte anual
// na fase de acumulação, retirada fixa depois da aposentadoria) para um
// custo (bps) estável.
const projetarPatrimonioIdade = (
  S,
  ages,
  idadeAposentadoria,
  custoBps,
  alfa
) => {
  const retirada = Number(S.retiradaAnual) || 0
  const aporte = Number(S.aporteAnual) || 0
  const serie = [S.auc]
  let p = S.auc
  for (let i = 1; i < ages.length; i++) {
    const retirando = ages[i] > idadeAposentadoria
    p =
      p * (1 + S.rent + alfa - custoBps / 10000) +
      (retirando ? -retirada : aporte)
    serie.push(Math.max(0, p))
  }
  return serie
}

// Projeção patrimonial por idade (ciclo de vida) — alimenta o gráfico
// "Evolução do patrimônio · ciclo de vida". Acumulação (só rentabilidade,
// sem aporte) da idade atual até a aposentadoria, retirada anual fixa
// depois. Independente de S.hor/S.mig (que regem só a "Projeção ano a ano",
// 1-5 anos): usa o custo já 100% migrado (steady state) dos 2 regimes
// comparados, porque a janela de migração é irrelevante numa curva de
// décadas. Reusa fbTotal/comisTotal já existentes — nenhuma fórmula nova.
export const simulateEvolucaoPatrimonio = (S) => {
  const P = S.P
  const custoComis = comisTotal(P).total
  const custoCons = fbTotal(P, S.alloc, 1, 1, 1)
  const { ages, idadeAposentadoria } = resolveIdadesCicloVida(S)
  const comis = projetarPatrimonioIdade(
    S,
    ages,
    idadeAposentadoria,
    custoComis,
    0
  )
  const cons = projetarPatrimonioIdade(
    S,
    ages,
    idadeAposentadoria,
    custoCons,
    S.alfa
  )
  const last = ages.length - 1
  const picoCons = cons.reduce((m, v) => Math.max(m, v), 0)
  return {
    ages,
    idadeAposentadoria,
    comis,
    cons,
    picoCons,
    comisFinal: comis[last],
    consFinal: cons[last],
    delta: cons[last] - comis[last],
  }
}

// Satura um ganho relativo (fração) em 0..1. Premissa a validar com produto.
const saturate = (x) => {
  const v = Number(x)
  if (!Number.isFinite(v) || v <= 0) return 0
  return v / (v + 0.15)
}

const clamp = (v, min, max) => {
  return Math.max(min, Math.min(max, v))
}

// Score heurístico 0–100 a partir dos outputs. Premissa a validar com produto:
// recompensa economia do cliente, ganho do assessor e cruzamento precoce.
export const computeScore = (R) => {
  if (!R) return 0
  const save = R.pctSave && R.pctSave.cliente ? R.pctSave.cliente : 0
  const adv = R.pctAdv && R.pctAdv.fee ? R.pctAdv.fee : 0
  const H = R.H || 0
  const crossFactor = R.cross && H ? (H - R.cross + 1) / H : 0
  const raw = 40 * saturate(save) + 40 * saturate(adv) + 20 * crossFactor
  return clamp(Math.round(raw), 0, 100)
}

// Deriva o status de elegibilidade a partir do score.
const scoreToStatus = (score) => {
  if (score >= 70) return 'elegivel'
  if (score >= 40) return 'em_potencial'
  return 'nao_elegivel'
}

// Calcula o Tier do cliente a partir do resultado R já calculado pelo simulate(S).
// Usa ganho de patrimônio (patrGain) que considera o alfa do serviço fiduciário.
// Tier A: Fee Assessoria é win-win (cliente ganha patrimônio E assessor ganha mais)
// Tier B: Fee não é win-win, mas Consultor CVM é win-win
// Tier C: Nenhum regime fee-based é win-win (manter comissionado)
export const calcularTier = (R) => {
  if (!R) return 'C'

  const patrGainFee = R.patrGain && R.patrGain.fee ? R.patrGain.fee : 0
  const patrGainCons = R.patrGain && R.patrGain.cons ? R.patrGain.cons : 0
  const advGainFee = R.advGain && R.advGain.fee ? R.advGain.fee : 0
  const advGainCons = R.advGain && R.advGain.cons ? R.advGain.cons : 0

  // Tier A: Fee Assessoria é win-win (cliente ganha patrimônio E assessor ganha)
  if (patrGainFee > 0 && advGainFee > 0) return 'A'

  // Tier B: Fee não é win-win, mas Consultor é win-win
  if (patrGainCons > 0 && advGainCons > 0) return 'B'

  // Tier C: Nenhum regime fee-based é win-win
  return 'C'
}

// Calcula o Tier para um cliente dado apenas o AuC (usa premissas padrão).
// Usado na listagem de clientes e dashboard onde não temos simulação individual.
export const calcularTierPorAuc = (auc) => {
  if (!auc || auc <= 0) return 'C'
  const S = { ...defaultLabState(), auc }
  const R = simulate(S)
  return calcularTier(R)
}

// Adapta o estado + resultado do lab ao contrato de save do pipeline.
export const labResultToAnalisePayload = (state, R) => {
  const inputs = structuredClone(state || {})
  // [D8] O canal vai NORMALIZADO para o JSON. `inputs` é clone do estado, e um
  // `undefined` ou um valor estranho gravado aqui faria o backend cair no ramo
  // "época antiga" — o PDF sairia 119,52 com a tela em 134,52. É a única linha
  // que separa a escolha do usuário do contrato de serialização.
  inputs.canal = canalNormalizado(state && state.canal)
  // ★ 12/08/2026 — a plataforma, pela mesma régua do canal, e o `fee_plat` que
  // ELA resolveu.
  //
  // Gravar só a escolha deixaria o cenário se contradizendo: `plataforma: btg`
  // ao lado de um `P.fee_plat: 15` que veio da premissa do escritório, e um
  // `costBreak.fee.fee_plat: 30` que veio da tabela. Três números, dois
  // valores. Aqui o `P` salvo passa a ser o que o motor de fato usou — o
  // cenário fica interno-consistente, e qualquer leitor (PDF, IA, reabertura)
  // chega ao mesmo número por qualquer caminho.
  inputs.plataforma = plataformaNormalizada(state && state.plataforma)
  inputs.P = premissasComATaxaDaPlataforma(state)
  inputs.patrimonio_total = state && state.auc ? state.auc : 0
  inputs.cenario_captacao = 'base'
  const resultados = structuredClone(R || {})
  const score = computeScore(R)
  return {
    inputs,
    resultados,
    score,
    status_elegibilidade: scoreToStatus(score),
    cenario_captacao: 'base',
  }
}
